import React from "react";
import { Button, Tooltip } from "antd";
import { LockOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { filter, isEmpty, map } from "lodash";
import { AiccPromptVariable } from "types/model/aiccPrompt";
import {
  DICTIONARY_DESCRIPTION,
  DICTIONARY_TITLE,
  INSERT_VARIABLE_HINT,
  NO_VARIABLES,
  OPTIONAL_VARIABLES_TITLE,
  REQUIRED_VARIABLES_TITLE,
} from "../configs";
import { promptVariableToken } from "../../PromptEditor/variables";
import styles from "./Reference.module.scss";

interface Props {
  variables: AiccPromptVariable[];
  onInsert?: (variable: AiccPromptVariable) => void;
}

function VariableRow({ variable, onInsert }: { variable: AiccPromptVariable; onInsert?: Props["onInsert"] }) {
  const { name, description, exampleValue, isRequired } = variable;

  return (
    <div className={isRequired ? styles.requiredVariable : styles.optionalVariable}>
      <div className={styles.variableHeader}>
        <span className={styles.variableName}>
          {promptVariableToken(name)}
          {isRequired && <span className={styles.requiredMark}>*</span>}
        </span>
        {!!onInsert && (
          <Tooltip title={INSERT_VARIABLE_HINT}>
            <Button
              type="link"
              size="small"
              className={styles.insertButton}
              icon={<PlusOutlined />}
              onMouseDown={(event) => {
                event.preventDefault();
                onInsert(variable);
              }}
            />
          </Tooltip>
        )}
      </div>
      <div className={styles.variableDescription}>{description}</div>
      {!!exampleValue && <div className={styles.variableExample}>{exampleValue}</div>}
    </div>
  );
}

function VariableDictionary({ variables, onInsert }: Props) {
  const required = filter(variables, "isRequired");
  const optional = filter(variables, ({ isRequired }) => !isRequired);

  return (
    <div className={styles.panel}>
      <div className={styles.panelTitle}>{DICTIONARY_TITLE}</div>
      <div className={styles.panelDescription}>{DICTIONARY_DESCRIPTION}</div>

      {isEmpty(variables) ? (
        <div className={styles.panelEmpty}>{NO_VARIABLES}</div>
      ) : (
        <>
          {!isEmpty(required) && (
            <div className={styles.variableGroup}>
              <div className={styles.variableGroupTitle}>
                <LockOutlined />
                {REQUIRED_VARIABLES_TITLE}
              </div>
              {map(required, (variable) => (
                <VariableRow key={variable.name} variable={variable} onInsert={onInsert} />
              ))}
            </div>
          )}

          {!isEmpty(optional) && (
            <div className={styles.variableGroup}>
              <div className={styles.variableGroupTitle}>
                <ThunderboltOutlined />
                {OPTIONAL_VARIABLES_TITLE}
              </div>
              {map(optional, (variable) => (
                <VariableRow key={variable.name} variable={variable} onInsert={onInsert} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default VariableDictionary;
